import { create } from "zustand";

/**
 * Shared decision-state store for requirement patch cards.
 *
 * Why this exists
 * ---------------
 * Acceptance is detected by matching the proposal against the live
 * `RequirementsSpec` cache (statement + category) — that's durable
 * across refresh because the requirement actually exists in Postgres
 * once accepted. Rejection has no equivalent server-side fact: the
 * user clicked "no" and we just drop the proposal. Without a shared
 * store, the rejection lived only in the per-card `useState`, which
 * meant the sibling `PatchContinueBar` couldn't tell rejected from
 * pending and the user got stuck with "1/2 resolved" after deciding
 * on both.
 *
 * What lives here
 * ---------------
 * A flat `{key: true}` map of rejected patches, persisted to
 * `localStorage` so a page refresh keeps the rejection visible. Keys
 * are derived from the patch content (see `patchRejectionKey`) and
 * are deliberately *content-addressed* — two patches with the same
 * content collide, which is fine: they're the same proposal.
 *
 * When to reset
 * -------------
 * Never automatically. The dataset is tiny (a few entries per
 * session) and clearing on every chat-session change risks losing a
 * rejection if the user does a quick session swap. If the store ever
 * grows past ~1k entries we'd want a cap or per-session scoping.
 */

interface DecisionState {
  /** Map of `key → true`; absent keys are not-yet-decided. */
  rejected: Record<string, true>;
  markRejected: (key: string) => void;
  isRejected: (key: string) => boolean;
}

const STORAGE_KEY = "cf-requirement-rejections";

function loadInitial(): Record<string, true> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      // Trust the shape — it's our own write. Cast through unknown to
      // satisfy TS without a runtime validator we don't need.
      return parsed as Record<string, true>;
    }
  } catch {
    /* corrupted; start fresh */
  }
  return {};
}

function persist(rejected: Record<string, true>) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rejected));
  } catch {
    /* quota / disabled storage — non-fatal; rejection only loses
       on refresh, which is the same as before this store existed */
  }
}

export const useDecisionStore = create<DecisionState>((set, get) => ({
  rejected: loadInitial(),
  markRejected: (key) =>
    set((s) => {
      if (s.rejected[key]) return s;
      const next = { ...s.rejected, [key]: true as const };
      persist(next);
      return { rejected: next };
    }),
  isRejected: (key) => Boolean(get().rejected[key]),
}));

/* ── Key derivation ──────────────────────────────────────────────────── */

/**
 * Stable key for a patch decision. Content-addressed so a refresh
 * keeps the rejection regardless of message id (which can change for
 * just-streamed messages that haven't been hydrated yet).
 *
 *   propose       → "p:<category>:<normalized-statement>"
 *   proposeUpdate → "u:<reqId>:<field>:<normalized-after>"
 */
export function patchRejectionKey(
  patch:
    | { kind: "propose"; preview: { category: string; statement: string } }
    | {
        kind: "proposeUpdate";
        requirementId: string;
        field: string;
        after: unknown;
      },
): string {
  if (patch.kind === "propose") {
    return `p:${patch.preview.category}:${normaliseForKey(patch.preview.statement)}`;
  }
  return `u:${patch.requirementId}:${patch.field}:${normaliseForKey(String(patch.after))}`;
}

function normaliseForKey(s: string): string {
  return s.replace(/\s+/g, " ").trim().toLowerCase().slice(0, 240);
}
